import { Banknote, CalendarRange, LineChart, Radar } from "lucide-react";
import ReactTooltip from "react-tooltip";
import { monthsLowerCase, monthsUpperCase } from "../../consts/forecast";
import { arrayHasItems, formatPrice } from "../../helpers/common";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

const getActiveMonthLimit = (selectedForecastYear) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // 1-12
    if (Number(selectedForecastYear) === currentYear) return currentMonth;
    if (Number(selectedForecastYear) < currentYear) return 12;
    return 0;
};

const getTotals = ({
    forecastProducts,
    selectedForecastYear
}) => {
    const monthLimit = getActiveMonthLimit(selectedForecastYear);
    const ytdLimit = (() => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1; // 1-12
        if (Number(selectedForecastYear) === currentYear) return Math.max(0, currentMonth - 1);
        if (Number(selectedForecastYear) < currentYear) return 12;
        return 0;
    })();
    const totalMonthsConsidered = monthLimit || 12;
    // Using active month limit for all YTD calculations
    // const totalClosedMonthsConsidered = closedMonthLimit || 12; // not used when counting all actual months
    if (!arrayHasItems(forecastProducts)) {
        return {
            onTrack: `0/${totalMonthsConsidered}`,
            sumByActuals: 0,
            sumByForecast: 0,
            totalVariancePct: 0,
            avgVariancePct: 0,
            bestMonthKey: null,
        }
    };
    
    const emptyMonths = (monthsLowerCase || []).reduce((acc, m) => ({ ...acc, [m]: 0 }), {});

    const sum = (arr) => arr.reduce((a, b) => a + b, 0);

    const sumByActualsMonthly = { ...emptyMonths };
    const sumByForecastMonthly = { ...emptyMonths };
    let sumByForecastYTDExclCurrent = 0;

    // Track per-product on-track counts and per-month on-track across products
    const perProductOnTrack = [];
    const variancePcts = [];
    const perMonthOnTrack = (monthsLowerCase || []).reduce((acc, m) => {
        acc[m] = { count: 0, totalActual: 0 };
        return acc;
    }, {});

    const toMonthIndex = (keyOrNumber) => {
        if (typeof keyOrNumber === "number") return keyOrNumber - 1; // 1-12 to 0-11
        return (monthsLowerCase || []).indexOf(String(keyOrNumber || "").toLowerCase());
    };

    (forecastProducts || []).forEach((product) => {
        const actuals = product?.monthly_actuals_by_month || {};
        const forecast = product?.monthly_forecast_by_month || {};
        const startIdx = toMonthIndex(product?.start_month);
        const endIdx = toMonthIndex(product?.end_month);

        let productOnTrackCount = 0;
        let productDenominator = 0;
        let productActualSum = 0;

        (monthsLowerCase || []).forEach((m, idx) => {
            const withinTotalsYTD = !monthLimit || idx < monthLimit;
            // const withinClosedYTD = !closedMonthLimit || idx < closedMonthLimit; // not used when counting all actual months
            const withinProductRange = (startIdx >= 0 && endIdx >= 0) ? (idx >= startIdx && idx <= endIdx) : (Number(forecast[m] || 0) > 0);

            const act = Number(actuals[m] || 0);
            const fc = Number(forecast[m] || 0);

            sumByForecastYTDExclCurrent += fc;
            // Aggregate totals and variance on active months (could include current month)
            if (withinTotalsYTD && withinProductRange) {
                sumByActualsMonthly[m] += act;
                sumByForecastMonthly[m] += fc;
                if (fc > 0) {
                    variancePcts.push(((act - fc) / fc) * 100);
                }
            }

            // OnTrack counted across all actual months (includes current month)
            if (withinTotalsYTD && withinProductRange) {
                productDenominator += 1;
                productActualSum += act;
                if (act >= fc) {
                    productOnTrackCount += 1;
                    perMonthOnTrack[m].count += 1;
                    perMonthOnTrack[m].totalActual += act;
                }
            }
        });

        perProductOnTrack.push({
            id: product?.id,
            count: productOnTrackCount,
            denom: productDenominator,
            actualSum: productActualSum,
        });
    });

    const sumByActuals = Object.values(sumByActualsMonthly).reduce((s, v) => s + Number(v || 0), 0);
    const sumByForecast = Object.values(sumByForecastMonthly).reduce((s, v) => s + Number(v || 0), 0);
    const totalVariancePct = sumByForecast > 0 ? ((sumByActuals - sumByForecast) / sumByForecast) * 100 : 0;
    const forecastToActualsPct = sumByActuals > 0 ? ((sumByForecast - sumByActuals) / sumByActuals) * 100 : 0;

    const avgVariancePct = variancePcts.length ? (sum(variancePcts) / variancePcts.length) : 0;

    // Determine On Track as the product with the most on-track months
    let bestProduct = null;
    perProductOnTrack.forEach((p) => {
        if (!bestProduct) {
            bestProduct = p;
            return;
        }
        if (p.count > bestProduct.count) {
            bestProduct = p;
            return;
        }
        if (p.count === bestProduct.count && p.actualSum > bestProduct.actualSum) {
            bestProduct = p;
        }
    });
    const onTrack = bestProduct ? `${bestProduct.count}/${totalMonthsConsidered}` : `0/${totalMonthsConsidered}`;

    // Determine Best Month as the month with the most products on-track (tie-break by total actual)
    let bestMonthKey = null;
    let bestMonthCount = -1;
    let bestMonthActual = 0;
    (monthsLowerCase || []).forEach((m, idx) => {
        const withinTotalsYTD = !monthLimit || idx < monthLimit;
        if (!withinTotalsYTD) return;
        const { count, totalActual } = perMonthOnTrack[m] || {};
        if (count > bestMonthCount || (count === bestMonthCount && totalActual > bestMonthActual)) {
            bestMonthCount = count;
            bestMonthActual = totalActual;
            bestMonthKey = count > 0 ? m : null;
        }
    });

    return {
        onTrack,
        sumByActuals,
        sumByForecast,
        sumByForecastYTDExclCurrent,
        totalVariancePct,
        forecastToActualsPct,
        avgVariancePct,
        bestMonthKey,
        bestMonthActual,
    };
}

export const TotalsSectionByForecastPlanningSystem = ({
    forecastProducts,
    selectedForecastYear
}) => {

    const {
        onTrack,
        sumByActuals,
        sumByForecast,
        sumByForecastYTDExclCurrent,
        totalVariancePct,
        forecastToActualsPct,
        avgVariancePct,
        bestMonthKey,
    } = getTotals({
        forecastProducts,
        selectedForecastYear
    });

    const forecastToActualsPctNumber = Number(forecastToActualsPct || 0);
    return (
        <section className="px-6 pt-6 flex flex-wrap gap-4 justify-between">
            <div className="rounded-xl border p-4 bg-blue-50 border-blue-100 flex-1 flex flex-col gap-2 items-start justify-center">
                <div className="flex flex-row gap-4 justify-between w-full">
                    <div className="text-gray-600 text-sm flex items-center gap-2 whitespace-nowrap">
                        <Radar className="h-6 w-6" />
                        <p>YTD Forecast</p>
                    </div>
                    <InformationCircleIcon 
                        data-tip={true} 
                        data-for="tooltip-total-forecast"
                        className="h-5 w-5 cursor-pointer" 
                    />
                    <ReactTooltip id="tooltip-total-forecast" html={true} effect='solid'>
                        {`<div>
                            <div><strong>Forecast</strong></div>
                            <div>
                                <div>
                                    <em>Total Forecast</em>: The total of all monthly forecasts within the active months for each product.
                                </div>
                                <div>
                                    <em>YTD Forecast</em>: The cumulative forecast from the start of the year up to the current month.  
                                    For past years, includes all 12 months. Each product’s active date range is respected.
                                </div>
                            </div>
                            <div>Sum of monthly forecasts over active months.</div>
                            <div><em>Active months:</em> current year = Jan..current month; past years = all 12.</div>
                            <div>Respects each product's active range (start/end month).</div>
                        </div>`}
                    </ReactTooltip>
                </div>
                <div className={`text-sm`}>
                    {`Total Forecast $${formatPrice(Number(sumByForecastYTDExclCurrent || 0))}`}
                </div>
                <div className="text-xl font-semibold">
                    ${formatPrice(Number(sumByForecast || 0))}
                </div>
            </div>
            <div className="rounded-xl border p-4 bg-green-50 border-green-100 flex-1 flex justify-between flex-col">
                <div className="flex flex-row gap-4 justify-between">
                    <div className="text-gray-600 text-sm flex items-center gap-2 whitespace-nowrap">
                        <Banknote className="h-6 w-6" />
                        <p>Total Actual</p>
                    </div>
                    <InformationCircleIcon 
                        data-tip={true} 
                        data-for="tooltip-total-actual"
                        className="h-5 w-5 cursor-pointer" 
                    />
                    <ReactTooltip id="tooltip-total-actual" html={true} effect='solid'>
                        {`<div>
                            <div><strong>Total Actual</strong></div>
                            <div>Sum of monthly actuals over active months.</div>
                            <div>Same active-month window as Total Forecast.</div>
                        </div>`}
                    </ReactTooltip>
                </div>
                <div className={`text-sm ${forecastToActualsPctNumber >= 0 ? "text-green-500" : "text-red-500"}`}>
                    {`Forecast to Actuals ${forecastToActualsPctNumber >= 0 ? "+" : ""}${forecastToActualsPctNumber.toFixed(2)}%`}
                </div>
                <div className="text-xl font-semibold">
                    ${formatPrice(Number(sumByActuals || 0))}
                </div>
            </div>
            <div className="rounded-xl border p-4 bg-indigo-50 border-indigo-100 flex-1 flex flex-col items-start justify-center">
                <div className="flex flex-row gap-4 justify-between">
                    <div className="text-gray-600 text-sm flex items-center gap-2 whitespace-nowrap">
                        <LineChart className="h-6 w-6" />
                        <p>Total Variance</p>
                    </div>
                    <InformationCircleIcon 
                        data-tip={true} 
                        data-for="tooltip-total-variance"
                        className="h-5 w-5 cursor-pointer" 
                    />
                    <ReactTooltip id="tooltip-total-variance" html={true} effect='solid'>
                        {`<div>
                            <div><strong>Total Variance %</strong></div>
                            <div>((Total Actual − Total Forecast) / Total Forecast) × 100</div>
                            <div>Totals are computed over active months and product active ranges.</div>
                        </div>`}
                    </ReactTooltip>
                </div>
                <div className="mt-1 text-xl font-semibold">
                    {`${Number(totalVariancePct || 0).toFixed(1)}%`}
                </div>
            </div>
            <div className="rounded-xl border p-4 bg-amber-50 border-amber-100 flex-1 flex flex-col items-start justify-center">
                <div className="flex flex-row gap-4 justify-between">
                    <div className="text-gray-600 text-sm flex items-center gap-2 whitespace-nowrap">
                        <div className="text-lg leading-none">%</div>
                        <p>Avg Variance</p>
                    </div>
                    <InformationCircleIcon 
                        data-tip={true} 
                        data-for="tooltip-avg-variance"
                        className="h-5 w-5 cursor-pointer" 
                    />
                    <ReactTooltip id="tooltip-avg-variance" html={true} effect='solid'>
                        {`<div>
                            <div><strong>Average Variance %</strong></div>
                            <div>Average of monthly ((Actual_m − Forecast_m) / Forecast_m) × 100</div>
                            <div>Includes only months with plan (Forecast_m &gt; 0), over active months.</div>
                        </div>`}
                    </ReactTooltip>
                </div>
                <div className="mt-1 text-xl font-semibold">
                    {`${Number(avgVariancePct || 0).toFixed(1)}%`}
                </div>
            </div>
            <div className="rounded-xl border p-4 bg-rose-50 border-rose-100 flex-1 flex flex-col items-start justify-center">
                <div className="flex flex-row gap-4 justify-between">
                    <div className="text-gray-600 text-sm flex items-center gap-2 whitespace-nowrap">
                        <CalendarRange className="h-6 w-6" />
                        <p>On Track</p>
                    </div>
                    <InformationCircleIcon 
                        data-tip={true} 
                        data-for="tooltip-on-track"
                        className="h-5 w-5 cursor-pointer" 
                    />
                    <ReactTooltip id="tooltip-on-track" html={true} effect='solid'>
                        {`<div>
                            <div><strong>On Track</strong></div>
                            <div>Choose the product with the most closed months where Actual ≥ Forecast.</div>
                            <div>Closed months: current year = Jan..previous month; past years = all 12.</div>
                            <div>Shown as: on_track_count / closed_months_considered_for_that_product</div>
                        </div>`}
                    </ReactTooltip>
                </div>
                <div className="mt-1 text-xl font-semibold">
                    {onTrack || "-"}
                </div>
            </div>
            <div className="rounded-xl border p-4 bg-cyan-50 border-cyan-100 flex-1 flex flex-col items-start justify-center">
                <div className="flex flex-row gap-4 justify-between">
                    <div className="text-gray-600 text-sm flex items-center gap-2 whitespace-nowrap">
                        <LineChart className="h-6 w-6" />
                        <p>Best Month</p>
                    </div>
                    <InformationCircleIcon 
                        data-tip={true} 
                        data-for="tooltip-best-month"
                        className="h-5 w-5 cursor-pointer" 
                    />
                    <ReactTooltip id="tooltip-best-month" html={true} effect='solid'>
                        {`<div>
                            <div><strong>Best Month</strong></div>
                            <div>Among closed months, pick the month with the most products on track (Actual ≥ Forecast).</div>
                        </div>`}
                    </ReactTooltip>                    
                </div>
                <div className="mt-1 text-xl font-semibold">
                    {bestMonthKey ? `${monthsUpperCase[(monthsLowerCase || []).indexOf(bestMonthKey)]}` : "-"}
                </div>
            </div>
        </section>
    )
}